/**
=========================================================
* Trust Cargo - Other Suppliers
=========================================================

* Suppliers that are not the Chinese ones: local shops, services, shipping,
* suppliers in other countries. A list of its own on purpose - the Suppliers
* page (the Chinese suppliers, their purchases and payments) reads another
* table, and nothing on this page reads or changes it.
*
* The list lives with the accounts on the server, so like Accounting it is
* for signed-in admins.
*/

import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import { FilterSelect } from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import RowActions from "components/RowActions";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

import { api, problemOf } from "layouts/accounting/api";
import VendorDialog, { KINDS } from "./VendorDialog";

const cell = { fontSize: 14, whiteSpace: "nowrap" };

const kindName = (id) => (KINDS.find((k) => k.id === id) || {}).name || id;

function Vendors() {
  const [rows, setRows] = useState(null);
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("");
  const [error, setError] = useState("");
  // {vendor} to edit, {} for new, null when closed
  const [editing, setEditing] = useState(null);

  const load = () =>
    api
      .get("/vendors")
      .then((data) => {
        setRows(Array.isArray(data) ? data : []);
        setError("");
      })
      .catch((e) => {
        setRows([]);
        setError(problemOf(e, "Could not load the suppliers"));
      });

  useEffect(() => {
    load();
  }, []);

  // the kind's name goes into the search too, so "خدمات" finds the service suppliers
  const searchable = (rows || []).map((r) => ({ ...r, kind_name: kindName(r.kind) }));
  const shown = filterRows(searchable, query).filter((r) => !kind || r.kind === kind);
  const pager = usePagination(shown);

  const remove = async (v) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${v.code} - ${v.name}?`)) return;
    try {
      await api.del(`/vendors/${v.id}`);
      load();
    } catch (e) {
      setError(problemOf(e, "Could not delete"));
    }
  };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <Card>
          <FilterBar
            title="Other Suppliers · موردين آخرين"
            value={query}
            onChange={setQuery}
            count={shown.length}
            total={(rows || []).length}
            placeholder="Search by code, name, city, phone..."
            actions={
              <Button variant="contained" color="primary" onClick={() => setEditing({})}>
                Add Supplier
              </Button>
            }
          >
            <FilterSelect
              label="Kind"
              icon="category"
              value={kind}
              onChange={setKind}
              options={KINDS.map((k) => ({ value: k.id, label: k.name }))}
            />
          </FilterBar>

          {error && (
            <SuiBox px={3} pb={1}>
              <SuiTypography variant="button" color="error">
                {error}
              </SuiTypography>
            </SuiBox>
          )}

          {rows === null ? (
            <CargoLoader label="Loading suppliers" />
          ) : (
            <>
              <SuiBox sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 900 }} aria-label="other suppliers">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Kind</TableCell>
                      <TableCell>Country / City</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="center">Operations</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pager.pageRows.map((v) => (
                      <TableRow key={v.id} sx={{ opacity: v.is_active ? 1 : 0.5 }}>
                        <TableCell sx={{ ...cell, fontWeight: 700 }}>{v.code}</TableCell>
                        <TableCell sx={{ ...cell, fontWeight: 600 }}>
                          {v.name}
                          {!v.is_active && (
                            <Chip
                              label="off"
                              size="small"
                              sx={{ ml: 1, height: 18, fontSize: 11 }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={cell}>{kindName(v.kind)}</TableCell>
                        <TableCell sx={cell}>
                          {[v.country, v.city].filter(Boolean).join(" · ") || "-"}
                        </TableCell>
                        <TableCell sx={cell}>
                          {[v.contact, v.phone].filter(Boolean).join(" · ") || "-"}
                        </TableCell>
                        <TableCell sx={cell}>{v.currency}</TableCell>
                        <TableCell align="center" sx={cell}>
                          <RowActions
                            actions={[
                              {
                                icon: "edit",
                                label: "Edit",
                                onClick: () => setEditing({ vendor: v }),
                              },
                              {
                                icon: "delete",
                                label: "Delete",
                                tone: "danger",
                                onClick: () => remove(v),
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {shown.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <SuiTypography variant="button" color="text">
                            {rows.length ? "No supplier matches" : "No suppliers yet"}
                          </SuiTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </SuiBox>
              <Pagination {...pager} />
            </>
          )}
        </Card>
      </SuiBox>

      <VendorDialog
        open={Boolean(editing)}
        vendor={editing?.vendor || null}
        onClose={() => setEditing(null)}
        onSaved={load}
      />
      <Footer />
    </DashboardLayout>
  );
}

export default Vendors;
